import { theme as antdTheme, type ThemeConfig } from 'antd';

/**
 * Personal-Area theme — LIGHT and ROOMY, and that is a decision rather than a taste.
 *
 * `docs/answers.md` **P7-defaults**: «Look and feel follows the LEGACY PORTAL's shape
 * — light, roomy, a big balance figure, one clear primary action per screen — and
 * explicitly NOT the admin UI's dark density.» The reason recorded there is a scoping
 * fact: `answers.md:30`'s «light and dense at the same time» begins with the words
 * "Admin UI", so `docs/ui/design-guidelines.md` and the `interface/` screenshots are
 * the ADMIN reference and there is no design decision for this portal anywhere else in
 * the corpus.
 *
 * ⚠ SO DO NOT "ALIGN" THIS FILE WITH `apps/captive-portal/src/theme.ts`. That sibling
 * imports the admin's `darkAlgorithm` + `compactAlgorithm` deliberately — an
 * enrollment portal is a 30-second interruption on somebody else's screen — while this
 * one is opened by a subscriber on a handset a few times a year. The two look
 * different on purpose.
 *
 * What "roomy" is made of here, since a token list is easy to flatten later:
 *   - NO compactAlgorithm;
 *   - fontSize 16, not 14 — the smallest size a phone renders comfortably without the
 *     browser zooming a focused input, which iOS does below 16 px;
 *   - controlHeight 44 — the touch-target floor, so a thumb hits the button and not
 *     the label above it.
 *
 * Colors live in tokens and are never hardcoded in a component (the rule the captive
 * portal's theme states). The primary is the product blue rather than the legacy
 * portal's yellow: the legacy's shape is what P7-defaults adopts, and yellow text
 * buttons fail contrast on white — the one thing a light theme must not do.
 */
export const colors = {
  bgLayout: '#f4f6f9',
  bgContainer: '#ffffff',
  bgElevated: '#ffffff',
  bgField: '#ffffff',
  bgSubtle: '#f7f9fc',
  border: '#d9e0e8',
  borderSubtle: '#e8edf3',
  primary: '#1a63c7',
  danger: '#cf1322',
  success: '#237804',
  warning: '#ad6800',
  text: '#1a2029',
  textMuted: '#5c6773',
} as const;

export const themeConfig: ThemeConfig = {
  algorithm: [antdTheme.defaultAlgorithm],
  cssVar: true,
  token: {
    colorPrimary: colors.primary,
    colorInfo: colors.primary,
    colorError: colors.danger,
    colorSuccess: colors.success,
    colorWarning: colors.warning,
    colorBgLayout: colors.bgLayout,
    colorBgContainer: colors.bgContainer,
    colorBgElevated: colors.bgElevated,
    colorBorder: colors.border,
    colorBorderSecondary: colors.borderSubtle,
    colorText: colors.text,
    colorTextSecondary: colors.textMuted,
    borderRadius: 10,
    fontSize: 16,
    controlHeight: 44,
    wireframe: false,
  },
  components: {
    Card: { colorBgContainer: colors.bgContainer },
    Input: { colorBgContainer: colors.bgField, paddingBlock: 10 },
    Button: { primaryShadow: 'none', defaultShadow: 'none', fontWeight: 600 },
    Form: { itemMarginBottom: 20 },
    Alert: { withDescriptionPadding: 16 },
  },
};
